import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState, useContext, useRef } from 'react'
import { Breadcrumbs, Button, LinearProgress, ListItemButton, Typography } from '@mui/material'

import HelpIcon from '../assets/icons/help-icon.svg'
import { Document, Payment, UserSessionType } from '../types'
import Dialog from '../components/common/dialog'
import { FormError } from '../types/common.types'
import { requestAccessToken } from '../api/auth.api'
import NextIcon from '../assets/icons/next-icon.svg'
import Pagination from '../components/common/pagination'
import { downloadPaymentVoucher } from '../api/payments.api'
import { getBatchPaymentDetailsData } from '../api/app-data.api'
import { AppStateContext, AuthContext, UserContext } from '../contexts'
import ActionsBar from '../components/document-payments-details-management/actions-bar'
import {downloadProofOfOperationPayments, downloadProofOfOperationPaymentsCSV, resolveDocumentsAccessToken } from '../api/documents.api'
import DocumentPaymentListItem from '../components/document-payments-details-management/document-payment-list-item'
import CustomerDetailsPaymentDetailsDataTable from '../components/customer-details-management/customer-details-payment-details-data-table'
import { getJsonSize, numberWithCommas, upsertEntityWithLimitAtStart, upsertManyWithLimitAtStartFast } from '../utils'
import { countriesCurrencies } from '../lib'
import { useSocket } from '../hooks/use-socket'
import { baseButtonBlueBg } from '../theme/custom-styles'
import { searchPaymentsByRegex } from '../api/app-queries.api'

const DocumentPaymentsDetailsManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const { documentId } = useParams()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { t } = useTranslation(['document-payments-details-management'])
  const { setScrollToTop, updateLoadingStatus } = useContext(AppStateContext)

  const currentListPageRef = useRef<number>(1)
  const paymentToSearchRef = useRef<string>('')
  const componentDidMount = useRef<boolean>(false)
  const dataFullyLoadedRef = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const currentPaymentsDataRef = useRef<Array<Payment>>([])
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [paymentsCount, setPaymentsCount] = useState<number>(0)
  const [amountOfPayment, setAmountOfPayment] = useState<number>(0)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [paymentToSearch, setPaymentToSearch] = useState<string>('')
  const [paymentsList, setPaymentsList] = useState<Array<Payment>>([])
  const [paymentsData, setPaymentsData] = useState<Array<Payment>>([])
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [documentData, setDocumentData] = useState<Partial<Document>>({})
  const [searchedPayments, setSearchedPayments] = useState<Array<Payment>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [paymentDetailsIsLoading, setPaymentDetailsIsLoading] = useState<boolean>(false)
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState<boolean>(false)
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<Partial<Payment>>({})
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    if (documentId) {
      appDataSocket.on(`updatePaymentsDetails:${documentId}`, async (dataFromSocket: any) => {
        if (dataFromSocket.deleted === true) {
          const paymentsDetailsDataUpdated = currentPaymentsDataRef.current.filter((paymentData: Payment) => String(paymentData._id) !== String(dataFromSocket._id))
          const batchPaymentDetailsData = globalThis.sessionStorage.getItem(`batchPaymentDetailsData:${String(documentId)}`)
          const batchPaymentDetailsDataToSet = JSON.parse(String(batchPaymentDetailsData))

          globalThis.sessionStorage.setItem(`batchPaymentDetailsData:${String(documentId)}`, JSON.stringify({ ...batchPaymentDetailsDataToSet, paymentsDataByBatchId: paymentsDetailsDataUpdated }))
          handleSetInitialData({ ...batchPaymentDetailsDataToSet, paymentsDataByBatchId: paymentsDetailsDataUpdated })
          return
        }

        const paymentsDetailsDataUpdated = upsertEntityWithLimitAtStart(currentPaymentsDataRef.current, dataFromSocket, 500)
        const batchPaymentDetailsData = globalThis.sessionStorage.getItem(`batchPaymentDetailsData:${String(documentId)}`)
        const batchPaymentDetailsDataToSet = JSON.parse(String(batchPaymentDetailsData))

        globalThis.sessionStorage.setItem(`batchPaymentDetailsData:${String(documentId)}`, JSON.stringify({ ...batchPaymentDetailsDataToSet, paymentsDataByBatchId: paymentsDetailsDataUpdated }))
        handleSetInitialData({ ...batchPaymentDetailsDataToSet, paymentsDataByBatchId: paymentsDetailsDataUpdated })
      })

      appDataSocket.on(`deletePaymentsDetails:${documentId}`, async (dataFromSocket: any) => {
        const paymentsDetailsDataUpdated = currentPaymentsDataRef.current.filter((paymentData: Payment) => String(paymentData._id) !== String(dataFromSocket.documentKey))
        const batchPaymentDetailsData = globalThis.sessionStorage.getItem(`batchPaymentDetailsData:${String(documentId)}`)
        const batchPaymentDetailsDataToSet = JSON.parse(String(batchPaymentDetailsData))

        globalThis.sessionStorage.setItem(`batchPaymentDetailsData:${String(documentId)}`, JSON.stringify({ ...batchPaymentDetailsDataToSet, paymentsDataByBatchId: paymentsDetailsDataUpdated }))
        handleSetInitialData({ ...batchPaymentDetailsDataToSet, paymentsDataByBatchId: paymentsDetailsDataUpdated })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetBatchPaymentDetailsData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleGetListElements(currentListPage, paymentsData, searchedPayments)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, paymentsData, searchedPayments])

  const handleGetBatchPaymentDetailsData = async () => {
    let batchPaymentDetailsDataToSet: any = {}
    const batchPaymentDetailsData = globalThis.sessionStorage.getItem(`batchPaymentDetailsData:${String(documentId)}`)

    if (batchPaymentDetailsData) {
      const JSONSize = getJsonSize(batchPaymentDetailsData)
      console.log('JSONSize', JSONSize)
      batchPaymentDetailsDataToSet = JSON.parse(batchPaymentDetailsData)
      handleSetInitialData(batchPaymentDetailsDataToSet)
    }

    handleGetBatchPaymentDetailsDataFromRemote()
  }

  const handleGetBatchPaymentDetailsDataFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getBatchPaymentDetailsData(String(documentId), accessToken)

      handleSetInitialData(data)
      globalThis.sessionStorage.setItem(`batchPaymentDetailsData:${String(documentId)}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetBatchPaymentDetailsData > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    if (dataToSet.batchData.currency !== currency) {
      navigate('/')
      return
    }

    const currentListPageDataState = currentListPageRef.current
    const currentPaymentToSearchState = paymentToSearchRef.current

    if (currentPaymentToSearchState === '' || currentPaymentToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const paymentsPerPage = dataToSet.paymentsDataByBatchId.slice(firstPageIndex, lastPageIndex)

      setPaymentsList(paymentsPerPage as Array<Payment>)
    }

    currentPaymentsDataRef.current = dataToSet.paymentsDataByBatchId
    setPaymentsCount(Number(dataToSet.paymentsDataStatsByBatchId[0].count))
    setAmountOfPayment(dataToSet.paymentsDataStatsByBatchId[0].totalAmount)
    setPaymentsData(dataToSet.paymentsDataByBatchId as Array<Payment>)
    setDocumentData({ status: dataToSet.batchData.status, originalName: dataToSet.batchData.originalName })

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }

    if (componentDidMount.current === false) {
      componentDidMount.current = true
    }

    dataFullyLoadedRef.current = true
  }

  const handleGetListElements = async (currentListPage: number, paymentsData: Array<Payment>, searchedPayments: Array<Payment>) => {
    setLoadingLocalData(true)
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = paymentToSearchRef.current === '' ? paymentsData.slice(firstPageIndex, lastPageIndex) : searchedPayments.slice(firstPageIndex, lastPageIndex)
    const paymentsPerPageToDisplay = []

    for (const element of paymentsPerPage) {
      const paymentData = element

      if (paymentData.customerData && Object.keys(paymentData.customerData).length === 0) {
        if (paymentData.customerId && !paymentData.customerRegisteredFullName) {
          const paymentCustomerData = paymentData.customerData
          paymentsPerPageToDisplay.push({ ...paymentData, customerData: paymentCustomerData })

          const paymentDataIndexInFullList = currentPaymentsDataRef.current.findIndex((paymentCurrentData) => String(paymentCurrentData._id) === String(paymentData._id))

          if (paymentDataIndexInFullList > -1) {
            currentPaymentsDataRef.current[paymentDataIndexInFullList].customerData = paymentCustomerData
          }

          setPaymentsData(currentPaymentsDataRef.current)
        } else if (paymentData.customerId && paymentData.customerRegisteredFullName) {
          paymentsPerPageToDisplay.push({
            ...paymentData,
            customerData: {
              registeredFullName: paymentData.customerRegisteredFullName
            }
          })

          const paymentDataIndexInFullList = currentPaymentsDataRef.current.findIndex((paymentCurrentData) => String(paymentCurrentData._id) === String(paymentData._id))

          if (paymentDataIndexInFullList > -1) {
            currentPaymentsDataRef.current[paymentDataIndexInFullList].customerData = {
              registeredFullName: paymentData.customerRegisteredFullName
            }
          }

          setPaymentsData(currentPaymentsDataRef.current)
        } else {
          paymentsPerPageToDisplay.push({
            ...paymentData,
            customerData: {
              registeredFullName: t('empty', { ns: 'payments-to-review-management' })
            }
          })

          const paymentDataIndexInFullList = currentPaymentsDataRef.current.findIndex((paymentCurrentData) => String(paymentCurrentData._id) === String(paymentData._id))

          if (paymentDataIndexInFullList > -1) {
            currentPaymentsDataRef.current[paymentDataIndexInFullList].customerData = {
              registeredFullName: t('empty', { ns: 'payments-to-review-management' })
            }
          }

          setPaymentsData(currentPaymentsDataRef.current)
        }
      } else {
        paymentsPerPageToDisplay.push(paymentData)
      }
    }

    setPaymentsList(paymentsPerPageToDisplay)

    if (componentDidMount.current === true) {
      setLoadingLocalData(false)
    }
  }

  const handleSetPaymentToSearch = async (paymentToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    paymentToSearchRef.current = paymentToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setNoRegistriesFoundAtLocal(false)
    setPaymentToSearch(paymentToSearchData)

    if (paymentToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentPaymentsDataState = currentPaymentsDataRef.current ?? []
      const paymentsPerPage = currentPaymentsDataState.slice(firstPageIndex, lastPageIndex)

      setPaymentsList(paymentsPerPage)
      setSearchedPayments([])

      if (showError) setErrors({ paymentToSearch: t('searchField.errors.emptyField') })
      return
    }

    const paymentsFromSearching = [] as Array<Payment>

    const currentFullPaymentsData = [...currentPaymentsDataRef.current]
    const filteredPayments = currentFullPaymentsData.filter((payment) => {
      const paymentStatus =
        payment.status.indexOf('error') !== -1
          ? t('paymentStatus.error')
          : payment.status === 'paid'
          ? t('paymentStatus.paid')
          : payment.status === 'processing'
          ? t('paymentStatus.processing')
          : payment.status === 'reviewed'
          ? t('paymentStatus.reviewed')
          : t('paymentStatus.forReview')

      return (
        paymentStatus.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        String(payment.amount).toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        payment.referenceNumber.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        payment.customerData?.documentNumber?.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        payment.customerData?.registeredFullName?.toLowerCase().includes(paymentToSearchData.toLowerCase())
      )
    })

    const fullFilteredPayments = filteredPayments.concat(paymentsFromSearching)
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentPerPage = fullFilteredPayments.slice(firstPageIndex, lastPageIndex)

    setPaymentsList(paymentPerPage)
    setSearchedPayments(fullFilteredPayments)

    if (fullFilteredPayments.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleDownloadCSVReport = async () => {
    updateLoadingStatus?.(true)

    try {
      setErrors({})
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const proofOfOperationPaymentsDownloaded = await downloadProofOfOperationPaymentsCSV(documentId ?? '', accessToken)
      window.open(proofOfOperationPaymentsDownloaded.data, '_blank')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    updateLoadingStatus?.(false)
  }

  const handleDownloadReport = async () => {
    updateLoadingStatus?.(true)

    try {
      setErrors({})
      const accessToken = await resolveDocumentsAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const proofOfOperationPaymentsDownloaded = await downloadProofOfOperationPayments(documentId ?? '', accessToken)
      window.open(proofOfOperationPaymentsDownloaded.data, '_blank')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    updateLoadingStatus?.(false)
  }

  const handleDownloadPaymentVoucher = async () => {
    try {
      setErrors({})
      setPaymentDetailsIsLoading(true)
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const paymentVoucherDownloaded = await downloadPaymentVoucher(String(selectedPaymentDetails._id), accessToken)

      window.open(paymentVoucherDownloaded.data, '_blank')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    setPaymentDetailsIsLoading(false)
  }

  const handleSearchBatchPaymentsDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchPaymentsByRegex({ searchText: paymentToSearch, batchId: documentId, status: 'inside-batch', belongsTo: userSession?.belongsTo, currency }, accessToken)

      if (data.paymentsDataByBatchId.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */
      data.paymentsDataByBatchId.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })
      setPaymentsList(data.paymentsDataByBatchId)
      setSearchedPayments(data.paymentsDataByBatchId)
      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */

      const currentPaymentsData = [...currentPaymentsDataRef.current]
      const paymentsDataUpdated = upsertManyWithLimitAtStartFast(currentPaymentsData, data.paymentsDataByBatchId, 1000)
      paymentsDataUpdated.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })

      currentPaymentsDataRef.current = paymentsDataUpdated
      setPaymentsData(paymentsDataUpdated)

      setSearchingInDatabase(false)
      setNoRegistriesFoundAtLocal(false)

      const batchPaymentDetailsData = globalThis.sessionStorage.getItem(`batchPaymentDetailsData:${String(documentId)}`)
      const batchPaymentDetailsDataToSet = JSON.parse(String(batchPaymentDetailsData))

      globalThis.sessionStorage.setItem(`batchPaymentDetailsData:${String(documentId)}`, JSON.stringify({ ...batchPaymentDetailsDataToSet, paymentsDataByBatchId: paymentsDataUpdated }))
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <>
      <div className="section-container">
        <Breadcrumbs
          separator={
            <img
              src={NextIcon}
              alt="next-icon"
            />
          }
          sx={{ width: '100%' }}
          aria-label="breadcrumb"
        >
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/documents-completed')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {documentData.status === 'dispersing' || documentData.status === 'register-disperse-completed' ? t('breadcrumbs.firstLevel') : t('breadcrumbs.secondLevel')}
            </Typography>
          </ListItemButton>
          <Typography
            key="3"
            className="breadcrumb-active"
          >
            {documentData.originalName ?? ''}
          </Typography>
        </Breadcrumbs>
        <ActionsBar
          errors={errors}
          userSession={userSession as UserSessionType}
          documentData={documentData}
          /* paymentsData={paymentsData} */
          paymentToSearch={paymentToSearch}
          searchingInDatabase={searchingInDatabase}
          handleDownloadReport={handleDownloadReport}
          handleClearSearchInput={() => setErrors({})}
          handleDownloadCSVReport={handleDownloadCSVReport}
          handleSetPaymentToSearch={handleSetPaymentToSearch}
          totalOfPayments={numberWithCommas(Number(paymentsCount))}
          amountOfPayments={numberWithCommas(Number(amountOfPayment).toFixed(2))}
        />
        <div className="table-header-container">
          <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg table-header-text">{t('tableHeaders.date')}</div>
          <div className="customer-company-table-column customer-company-table-width-45 table-header-text">{t('tableHeaders.beneficiary')}</div>
          <div className="customer-company-table-column customer-company-table-width-15 table-header-text customer-company-display-none-lg">{t('tableHeaders.amount')}</div>
          <div className="customer-company-table-column customer-company-table-width-20 table-header-text customer-company-display-none-sm">{t('tableHeaders.referenceNumber')}</div>
          <div className="customer-company-table-column customer-company-table-width-10 table-header-text">{t('tableHeaders.status')}</div>
        </div>
        {loadingLocalData ? (
          <div className="documents-list-container">
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
            <div className="documents-list-item-data-container-loader"></div>
          </div>
        ) : (
          <div className="documents-list-container">
            {paymentsList.map((paymentData: Payment) => {
              return (
                <DocumentPaymentListItem
                  key={paymentData._id}
                  showDateData
                  showCustomerData
                  paymentData={paymentData}
                  handleOpenPaymentDetailsModal={(selectedPaymentData: Partial<Payment>) => {
                    if (selectedPaymentData.status === 'waiting-for-resolution') {
                      navigate(`/payment-to-review-details/${String(selectedPaymentData._id)}`)
                      return
                    }

                    setOpenPaymentDetailsModal(true)
                    setSelectedPaymentDetails(selectedPaymentData)
                  }}
                />
              )
            })}
          </div>
        )}
        <Pagination
          pageSize={pageSize}
          currentPage={currentListPage}
          handlePaginationPageChange={(page: number) => {
            setScrollToTop(true)
            currentListPageRef.current = page
            setCurrentListPage(page)
          }}
          handleChangePageSize={(pageSizeToSet: string) => {
            setScrollToTop(true)
            setPageSize(Number(pageSizeToSet))
          }}
          totalCount={paymentToSearch === '' ? paymentsData.length : searchedPayments.length}
        />
        {noRegistriesFoundAtLocal && (
          <div className="customer-no-data-found-container">
            {searchingInDatabase ? (
              <>
                <div className="customer-no-data-found-searching-progress-bar">
                  <LinearProgress />
                </div>
                <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3', { ns: 'customer-company-details-management' })}</div>
              </>
            ) : (
              <>
                {noRegistriesFoundAtDatabase === false ? (
                  <>
                    <div className="customer-no-data-found-no-registries-text">
                      {t('searchingResultTexts.case1.firstPart', { ns: 'customer-company-details-management' })}{' '}
                      <strong
                        className="customer-no-data-found-no-registries-text-click"
                        onClick={() => handleSearchBatchPaymentsDataByRegex()}
                      >
                        {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                      </strong>{' '}
                      {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                    </div>
                    <Button
                      sx={baseButtonBlueBg}
                      onClick={handleSearchBatchPaymentsDataByRegex}
                    >
                      {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                    </Button>
                    {errors.generalError && (
                      <div
                        style={{ marginTop: '0px' }}
                        className="error-message"
                      >
                        {errors.generalError}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="customer-no-data-found-no-registries-text">
                      <img
                        src={HelpIcon}
                        alt="help-icon"
                      />
                    </div>
                    <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customer-company-details-management' })}</div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {openPaymentDetailsModal && (
        <Dialog
          errors={errors}
          dialogIsLoading={paymentDetailsIsLoading}
          dialogTitle={t('paymentDetailsModal.title')}
          handleDialogAction={handleDownloadPaymentVoucher}
          handleCloseOptionAction={() => {
            setOpenPaymentDetailsModal(false)
            setSelectedPaymentDetails({})
          }}
          handleDialogOpenState={(openState: boolean) => {
            setOpenPaymentDetailsModal(openState)
            setSelectedPaymentDetails({})
          }}
          actionButtonText={t('paymentDetailsModal.actionButtonText')}
          hideActionsButtons={
            selectedPaymentDetails.status === 'processing' || selectedPaymentDetails.status === 'waiting-for-resolution' || (selectedPaymentDetails.status === 'reviewed' && selectedPaymentDetails.documentId !== '')
          }
        >
          <CustomerDetailsPaymentDetailsDataTable paymentDetailsData={selectedPaymentDetails} />
        </Dialog>
      )}
    </>
  )
}

export default DocumentPaymentsDetailsManagement
